import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { BottlingEventStatus, BottlingSku } from "@/lib/bottling-constants";

// ─── Queries ───────────────────────────────────────────────────────────────

export function useBottlingAdminEvents() {
  return useQuery({
    queryKey: ["bottling-events-admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bottling_event")
        .select(`
          id, name, status, confirmed_date, est_cases,
          preferred_date_from, preferred_date_to,
          client:user_account!bottling_event_client_id_fkey (
            id, display_name, email
          )
        `)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useBottlingClientEvents(userId: string | undefined) {
  return useQuery({
    queryKey: ["bottling-events-client", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bottling_event")
        .select("id, name, status, preferred_date_from, preferred_date_to, confirmed_date, est_cases, created_at")
        .eq("client_id", userId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useBottlingEvent(eventId: string | undefined) {
  return useQuery({
    queryKey: ["bottling-event", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bottling_event")
        .select("*")
        .eq("id", eventId!)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export function useBottlingSkus(eventId: string | undefined) {
  return useQuery({
    queryKey: ["bottling-skus", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bottling_sku")
        .select("*")
        .eq("event_id", eventId!)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });
}

export function useBottlingRevisions(eventId: string | undefined) {
  return useQuery({
    queryKey: ["bottling-revisions", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bottling_revision_log")
        .select(`
          *,
          changed_by_user:user_account!bottling_revision_log_changed_by_fkey (
            display_name
          )
        `)
        .eq("event_id", eventId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useBottlingPastEvents(userId: string | undefined) {
  return useQuery({
    queryKey: ["bottling-past-events", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bottling_event")
        .select("id, name, status, confirmed_date, est_cases, preferred_date_from")
        .eq("client_id", userId!)
        .eq("status", "completed")
        .order("confirmed_date", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useUpdateBottlingEventStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      eventId,
      status,
      extraFields = {},
    }: {
      eventId: string;
      status: BottlingEventStatus;
      extraFields?: Record<string, unknown>;
    }) => {
      const updates: Record<string, unknown> = { status, ...extraFields };
      if (status === "submitted") updates.submitted_at = new Date().toISOString();
      if (status === "confirmed") updates.confirmed_at = new Date().toISOString();
      if (status === "locked") updates.locked_at = new Date().toISOString();

      const { error } = await supabase
        .from("bottling_event")
        .update(updates)
        .eq("id", eventId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bottling-events-admin"] });
      queryClient.invalidateQueries({ queryKey: ["bottling-events-client"] });
      queryClient.invalidateQueries({ queryKey: ["bottling-event"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useLogBottlingRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      eventId,
      changedBy,
      changeType,
      summary,
      payload = null,
    }: {
      eventId: string;
      changedBy: string;
      changeType: string;
      summary: string;
      payload?: unknown;
    }) => {
      const { error } = await supabase.from("bottling_revision_log").insert({
        event_id: eventId,
        changed_by: changedBy,
        change_type: changeType,
        summary,
        payload,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bottling-revisions"] });
    },
  });
}

export function useUpdateBottlingSkus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      eventId,
      skus,
      orgId,
    }: {
      eventId: string;
      skus: BottlingSku[];
      orgId?: string;
    }) => {
      await supabase.from("bottling_sku").delete().eq("event_id", eventId);

      if (skus.length > 0) {
        const rows = skus.map((s, i) => ({
          event_id: eventId,
          org_id: orgId,
          sort_order: i,
          brand: s.brand,
          wine: s.wine,
          vintage: s.vintage,
          bottle_type: s.bottle,
          bottle_format: s.format || "750ml",
          closure: s.closure,
          capsule: s.capsule === "Yes",
          case_pack: s.casePack,
          est_cases: s.cases,
          filter_type: s.filter,
          dmdc: s.dmdc === "Yes",
          label: s.label || "Yes",
          fanciful_name: s.fanciful || null,
          ava: s.ava || null,
          lot_code: s.lotCode || null,
          instructions: s.instructions,
        }));
        const { error } = await supabase.from("bottling_sku").insert(rows);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bottling-skus"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useSubmitBottlingEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      orgId,
      formData,
      skus,
    }: {
      userId: string;
      orgId: string;
      formData: { name: string; facility: string; dateFrom: string; dateTo: string; clientNotes: string };
      skus: BottlingSku[];
    }) => {
      const eventData = {
        name: formData.name,
        status: "submitted",
        org_id: orgId,
        client_id: userId,
        facility: formData.facility,
        preferred_date_from: formData.dateFrom || null,
        preferred_date_to: formData.dateTo || null,
        preferred_date_notes: formData.clientNotes,
        client_notes: formData.clientNotes,
        est_cases: skus.reduce((s, x) => s + Number(x.cases), 0),
        submitted_at: new Date().toISOString(),
      };

      const { data: evt, error: evtErr } = await supabase
        .from("bottling_event")
        .insert(eventData)
        .select()
        .single();
      if (evtErr) throw evtErr;

      if (skus.length > 0) {
        const rows = skus.map((s, i) => ({
          event_id: evt.id,
          org_id: orgId,
          sort_order: i,
          brand: s.brand,
          wine: s.wine,
          vintage: s.vintage,
          bottle_type: s.bottle,
          bottle_format: s.format || "750ml",
          closure: s.closure,
          capsule: s.capsule === "Yes",
          case_pack: s.casePack,
          est_cases: s.cases,
          filter_type: s.filter,
          dmdc: s.dmdc === "Yes",
          label: s.label || "Yes",
          fanciful_name: s.fanciful || null,
          ava: s.ava || null,
          lot_code: s.lotCode || null,
          instructions: s.instructions,
        }));
        const { error: skuErr } = await supabase.from("bottling_sku").insert(rows);
        if (skuErr) throw skuErr;
      }

      await supabase.from("bottling_revision_log").insert({
        event_id: evt.id,
        changed_by: userId,
        change_type: "status_change",
        summary: "Event submitted by client.",
      });

      return evt;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bottling-events-client"] });
      queryClient.invalidateQueries({ queryKey: ["bottling-events-admin"] });
      toast({ title: "Success", description: "Bottling event submitted." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useSaveBottlingDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      orgId,
      formData,
      skus,
    }: {
      userId: string;
      orgId: string;
      formData: { name: string; facility: string; dateFrom: string; dateTo: string; clientNotes: string };
      skus: BottlingSku[];
    }) => {
      const eventData = {
        name: formData.name || "Draft",
        status: "draft",
        org_id: orgId,
        client_id: userId,
        facility: formData.facility,
        preferred_date_from: formData.dateFrom || null,
        preferred_date_to: formData.dateTo || null,
        client_notes: formData.clientNotes,
        est_cases: skus.reduce((s, x) => s + Number(x.cases), 0),
      };

      const { data: evt, error } = await supabase
        .from("bottling_event")
        .insert(eventData)
        .select()
        .single();
      if (error) throw error;

      if (skus.length > 0) {
        const rows = skus.map((s, i) => ({
          event_id: evt.id,
          org_id: orgId,
          sort_order: i,
          brand: s.brand,
          wine: s.wine,
          vintage: s.vintage,
          bottle_type: s.bottle,
          bottle_format: s.format || "750ml",
          closure: s.closure,
          capsule: s.capsule === "Yes",
          case_pack: s.casePack,
          est_cases: s.cases,
          filter_type: s.filter,
          dmdc: s.dmdc === "Yes",
          label: s.label || "Yes",
          fanciful_name: s.fanciful || null,
          ava: s.ava || null,
          lot_code: s.lotCode || null,
          instructions: s.instructions,
        }));
        await supabase.from("bottling_sku").insert(rows);
      }

      return evt;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bottling-events-client"] });
      toast({ title: "Success", description: "Draft saved." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useExportBottlingEvents() {
  return useMutation({
    mutationFn: async ({
      status,
      dateFrom,
      dateTo,
    }: {
      status: string;
      dateFrom?: string;
      dateTo?: string;
    }) => {
      let query = supabase
        .from("bottling_event")
        .select(`*, client:user_account!bottling_event_client_id_fkey(display_name, email), bottling_sku(*)`)
        .order("confirmed_date", { ascending: false });

      if (status !== "all") query = query.eq("status", status);
      if (dateFrom) query = query.gte("confirmed_date", dateFrom);
      if (dateTo) query = query.lte("confirmed_date", dateTo);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}
